import { useEffect, useMemo, useState } from 'react';
import { SecurityModel } from 'api';
import { Security } from 'common/types/company';
import { SecurityOption, UseAllSecurityOptionsProps } from './useAllSecurityOptions.props';

export const useAllSecurityOptions = ({
  companyInfo,
  captableInfo,
  capTableVersionsByCompany,
}: UseAllSecurityOptionsProps) => {
  const [securityOptionsByEntity, setSecurityOptionsByEntity] = useState<Record<number, SecurityOption[]>>({});
  const [loading, setLoading] = useState(true);

  const childCompanyIdSecurityMap = useMemo<{ [key: number]: Security[] }>(
    () =>
      Object.values(capTableVersionsByCompany).reduce((acc, holdingCompCaptable) => {
        if (!holdingCompCaptable.length) {
          return acc;
        }

        const primary =
          holdingCompCaptable.find(ct => ct.is_primary)
          ?? holdingCompCaptable.find(ct => (ct.securities?.length ?? 0) > 0)
          ?? holdingCompCaptable[0];

        if (primary && primary.company_id) {
          acc[primary.company_id] = primary.securities || [];
        }
        return acc;
      }, {} as { [key: number]: Security[] }),
    [capTableVersionsByCompany]
  );

  useEffect(() => {
    setSecurityOptionsByEntity({
      [companyInfo?.id || 0]: (captableInfo?.securities as SecurityModel[] || []).map(
        (security: SecurityModel) => ({
          id: security.id,
          value: security.id,
          label: security.name,
        })
      ),
      ...Object.entries(childCompanyIdSecurityMap).reduce((acc, [childCompanyId, securities]) => {
        acc[Number(childCompanyId)] = securities.map((security: Security) => ({
          id: security.id || 0,
          value: security.id || 0,
          label: security.name || '',
        }));
        return acc;
      }, {} as Record<number, SecurityOption[]>),
    });
    setLoading(false);
  }, [companyInfo, captableInfo, childCompanyIdSecurityMap]);

  return useMemo(
    () => ({
      securityOptionsByEntity,
      loading,
    }),
    [securityOptionsByEntity, loading]
  );
};
